import { useEffect, useState } from "react";
import { endpoints } from "config";
import { ColumnsMaxWidths } from "components/Entity/Entity";
import { useWindowSize } from "helpers/useWindowSize";
import { updateExchangeRate } from "./Invoice.utils";
import { FormDatasourceData } from "./Invoice.datasource";
import { getEditValidationSchema } from "./Invoice.validation";
import {
  rightsRendererUpdateWithUpdateDocumentPermissions,
  rightsRendererUpdateWithoutUpdateDocumentPermissions,
} from "./Invoice.renderers";
import { InvoiceActionType } from "./Invoice.types";
import { actionTypesWithUpdateDocumentPermissions } from "./Invoice.config";

/**
 * Consolidated hook for Invoice component state and logic
 * Manages responsive layout, exchange rate state, and renderer selection
 */
export const useInvoice = (actionType: InvoiceActionType, homeModule?: boolean) => {
  // State management
  const [columnsMaxWidths, setColumnsMaxWidths] = useState<ColumnsMaxWidths | null>(null);
  const [formDatasourceData, setFormDatasourceData] = useState<FormDatasourceData | null>(null);
  const [lastInvoiceCurrencyID, setLastInvoiceCurrencyID] = useState<number>();
  const [lastExchangeCurrencyID, setLastExchangeCurrencyID] = useState<number>();
  const [lastExchangeRate, setLastExchangeRate] = useState<string | number>();
  const [lastInvoiceAmount, setLastInvoiceAmount] = useState<string | number>();

  const [width] = useWindowSize();

  // Validation schema
  const validationSchemaViewUpdate = getEditValidationSchema(formDatasourceData, actionType);

  // Responsive layout effect
  useEffect(() => {
    if (actionType.includes("view") || actionType.includes("update")) {
      if (width <= 900) {
        setColumnsMaxWidths({ 1: "100%" });
      } else if (width > 900) {
        setColumnsMaxWidths({ 1: "60%" });
      }
    } else {
      setColumnsMaxWidths(null);
    }
  }, [width, actionType]);

  // Initial data URL determination
  const getInitialDataUrl = (): string => {
    if (actionType === "view") {
      return endpoints.invoices.view;
    } else if (actionType === "edit") {
      return endpoints.invoices.edit;
    } else if (actionType === "update-productmanager") {
      return homeModule
        ? endpoints.home.invoicesEditProjectManager
        : endpoints.invoices.editProjectManager;
    } else if (actionType === "update-apclerk") {
      return homeModule ? endpoints.home.invoicesEditApClerk : endpoints.invoices.editApClerk;
    } else if (actionType === "update-apcoordinator") {
      return homeModule ? endpoints.home.invoicesEditFinance : endpoints.invoices.editFinance;
    } else if (actionType === "update") {
      return endpoints.invoices.edit;
    } else if (actionType === "create") {
      return endpoints.invoices.create;
    } else if (actionType === "upload") {
      return endpoints.invoices.upload;
    }
    return "";
  };

  // Renderer selection
  const getFinalUpdateRightsRenderer = () => {
    return actionTypesWithUpdateDocumentPermissions.includes(actionType)
      ? rightsRendererUpdateWithUpdateDocumentPermissions
      : rightsRendererUpdateWithoutUpdateDocumentPermissions;
  };

  // Exchange rate update handler
  const handleUpdateExchangeRate = (values: any) => {
    updateExchangeRate(
      values,
      lastInvoiceCurrencyID,
      setLastInvoiceCurrencyID,
      lastExchangeCurrencyID,
      setLastExchangeCurrencyID,
      lastExchangeRate,
      setLastExchangeRate,
      lastInvoiceAmount,
      setLastInvoiceAmount
    );
  };

  return {
    columnsMaxWidths,
    formDatasourceData,
    setFormDatasourceData,
    validationSchemaViewUpdate,
    initialDataUrl: getInitialDataUrl(),
    finalUpdateRightsRenderer: getFinalUpdateRightsRenderer(),
    handleUpdateExchangeRate,
  };
};
